import { Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmTextareaImports } from '@spartan-ng/helm/textarea';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ButtonAddNewComponent } from '../../shared/components/button-add-new/button-add-new.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../shared/components/toastr/toastr-success.component';
import { LabelRequiredDirective } from '../../shared/directives/label-required.directive';
import { FormationObligatoireReglementaire } from '../models/formation.model';
import { NiveauFormation } from '../models/niveau-formation.model';
import { FormationCreateRequest } from '../requests/formation-create-request.model';
import { FormationReglementaireObligatoireStore } from '../store/for.store';

@Component({
  selector: 'app-form-formation-obligatoire-reglementaire',
  imports: [
    ReactiveFormsModule,
    HlmFieldImports,
    HlmInputImports,
    ButtonAddNewComponent,
    ExpansionPanel,
    HlmTextareaImports,
    LabelRequiredDirective,
  ],
  templateUrl: './form-formation-obligatoire-reglementaire.component.html',
})
export class FormFormationObligatoireReglementaireComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #formationStore = inject(FormationReglementaireObligatoireStore);

  readonly idCartographie = input.required<string>();
  readonly formation = input<FormationObligatoireReglementaire>();

  readonly form = this.#fb.group({
    code: [null, [Validators.required]],
    nom: [null, [Validators.required]],
    description: [null],
    validite: [null],
    public: [null],
    prerequis: [null],
    niveaux: this.#fb.array([]),
  });

  readonly niveauActions: ExpansionPanelMenuAction<number>[] = [
    {
      icon: 'lucideTrash',
      invisible: (idx) => idx === 0,
      label: 'Supprimer',
      action: (idx) => this.niveaux.removeAt(idx!),
    },
  ];

  constructor() {
    effect(() => {
      const formation = this.formation();

      if (formation) {
        this.code.setValue(formation.code);
        this.nom.setValue(formation.nom);
        this.description.setValue(formation.description);
        this.validite.setValue(formation.validite);
        this.public.setValue(formation.public);
        this.prerequis.setValue(formation.prerequis);
        this.niveaux.clear();
        formation.niveaux.forEach((x) => this.ajouterNiveau(x));
      }
    });
  }

  ngOnInit(): void {
    this.ajouterNiveau();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  ajouterNiveau(niveau?: NiveauFormation) {
    const niveauFormGroup = this.#fb.group({
      nom: [niveau?.nom, [Validators.required]],
      duree: [niveau?.duree, [Validators.required]],
    });
    this.niveaux.push(niveauFormGroup);
  }

  onSubmit() {
    const toastrId = uuidv7();

    if (this.form.invalid) return;

    const data: FormationCreateRequest = {
      idCartographie: this.idCartographie(),
      code: this.code.value,
      nom: this.nom.value,
      description: this.description.value,
      validite: this.validite.value,
      public: this.public.value,
      prerequis: this.prerequis.value,
      niveaux: this.niveaux.value,
    };
    const formation = this.formation();

    if (formation) {
      this.#formationStore.modifier({
        id: formation.id,
        data,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: 'Modification de la formation réussie',
              id: toastrId,
            },
            id: toastrId,
          });
          this.#sidepanelService.close();
          this.#formationStore.charger(this.idCartographie());
        },
      });
    } else {
      this.#formationStore.ajouter({
        nouvelleFormation: data,
        next: () => {
          toast.custom(ToastrSuccessComponent, {
            componentProps: {
              message: 'Création de la formation réussie',
              id: toastrId,
            },
            id: toastrId,
          });
          this.#sidepanelService.close();
          this.#formationStore.charger(this.idCartographie());
        },
      });
    }
  }

  get code() {
    return this.form.get('code') as AbstractControl;
  }

  get nom() {
    return this.form.get('nom') as AbstractControl;
  }

  get description() {
    return this.form.get('description') as AbstractControl;
  }

  get validite() {
    return this.form.get('validite') as AbstractControl;
  }

  get public() {
    return this.form.get('public') as AbstractControl;
  }

  get prerequis() {
    return this.form.get('prerequis') as AbstractControl;
  }

  get niveaux() {
    return this.form.get('niveaux') as FormArray<FormGroup>;
  }
}
